import type { FocusEventHandler, ReactNode } from "react";
import { WorkToastQuestionOptions } from "@/components/toasts/work-toast-question-options";
import type {
  WorkToastQuestion,
  WorkToastQuestionOption,
} from "@/stores/work-toast-store";
import { useT } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";

/** The live question and its measurement clone use the same box geometry. */
export function CompactCursorPillQuestionLayout({
  question,
  disabled,
  onPickOption,
  optionTestIdPrefix,
  testId,
  showReply,
  reply,
  onFocus,
  onBlur,
}: {
  question: WorkToastQuestion;
  disabled: boolean;
  onPickOption: (option: WorkToastQuestionOption) => void;
  optionTestIdPrefix: string;
  testId?: string;
  showReply: boolean;
  reply: ReactNode;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  onBlur?: FocusEventHandler<HTMLDivElement>;
}) {
  const t = useT(compactCursorMessages);
  const { index, count } = question;
  const hasStep = typeof index === "number" && typeof count === "number";
  return (
    <div className="flex w-[289px] min-w-0 flex-col gap-2" data-testid={testId}>
      {hasStep && (
        <span className="text-appkit-mini font-semibold uppercase tracking-wide text-muted">
          {t("questionStep", { index, count })}
        </span>
      )}
      <p className="text-appkit-body text-primary">{question.question}</p>
      {question.context && (
        <p className="text-appkit-small text-muted">{question.context}</p>
      )}
      <WorkToastQuestionOptions
        options={question.options}
        disabled={disabled}
        onPickOption={onPickOption}
        testIdPrefix={optionTestIdPrefix}
      />
      <div
        className="min-w-0"
        hidden={!showReply}
        inert={!showReply}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-hidden={!showReply}
      >
        {reply}
      </div>
    </div>
  );
}
